import React, { useMemo, useState } from 'react';
import {
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ExclamationTriangleIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAdminRequests } from '../../hooks/useAdminRequests';
import {
  type AdminRequestBucket,
  type AdminRequestArchiveReasonCode,
  type AdminRequestArchiveView,
  type AdminRequestDirectoryItem,
  type AdminRequestsMetric,
  filterRequestsByArchiveView,
} from '../../services/adminRequestsService';
import { RepairStatus } from '../../types/firestore';
import {
  REPAIR_STATUS_ORDER,
  getRepairRequestStatusLabel,
} from '../../utils/repairRequestWorkflow';

type BucketFilter = AdminRequestBucket | 'all';
type StatusFilter = RepairStatus | 'all';
type FulfillmentFilter = 'all' | 'customerDropoff' | 'servicePickup' | 'onsiteService' | 'missing';
type CreatedRangeFilter = '7d' | '30d' | '90d' | 'all';
type SortOption = 'updated_desc' | 'created_desc' | 'created_asc' | 'status_asc' | 'index_asc';
type ArchiveModalState =
  | { kind: 'archive'; request: AdminRequestDirectoryItem }
  | { kind: 'archiveBatch'; requests: AdminRequestDirectoryItem[] }
  | { kind: 'restore'; request: AdminRequestDirectoryItem }
  | null;

const ARCHIVE_REASON_OPTIONS: Array<{
  value: AdminRequestArchiveReasonCode;
  label: string;
}> = [
  { value: 'test_cleanup', label: 'Porządki po testach' },
  { value: 'orphaned_service', label: 'Osierocony serwis' },
  { value: 'broken_legacy', label: 'Uszkodzony legacy' },
  { value: 'duplicate', label: 'Duplikat' },
  { value: 'other', label: 'Inny powód' },
];

const statusOrder = REPAIR_STATUS_ORDER.reduce(
  (acc, status, index) => ({ ...acc, [status]: index }),
  {} as Record<string, number>
);

const formatMetricValue = (metric: AdminRequestsMetric): string =>
  metric.value === null ? '—' : String(metric.value);

const Badge: React.FC<{
  children: React.ReactNode;
  tone?: 'default' | 'good' | 'warning' | 'danger' | 'neutral';
}> = ({ children, tone = 'default' }) => {
  const toneClass =
    tone === 'good'
      ? 'border-ratuj-300/30 bg-ratuj-300/10 text-ratuj-100'
      : tone === 'warning'
        ? 'border-amber-300/30 bg-amber-300/10 text-amber-100'
        : tone === 'danger'
          ? 'border-status-error-500/35 bg-status-error-500/10 text-status-error-300'
          : tone === 'neutral'
            ? 'border-primary-dark-300 bg-primary-dark-100 text-primary-light-400'
            : 'border-primary-dark-300/70 bg-primary-dark-200 text-primary-light-300';

  return (
    <span
      className={`inline-flex max-w-full items-center rounded-md border px-2 py-0.5 text-[11px] font-medium ${toneClass}`}
    >
      {children}
    </span>
  );
};

const MetricTile: React.FC<{ metric: AdminRequestsMetric; tone?: 'good' | 'warning' }> = ({
  metric,
  tone,
}) => {
  const toneClass =
    tone === 'good'
      ? 'border-ratuj-300/30 bg-ratuj-300/10'
      : tone === 'warning'
        ? 'border-amber-300/30 bg-amber-300/10'
        : 'border-primary-dark-300/18 bg-primary-dark-100/10';

  return (
    <article className={`rounded-lg border p-3.5 ${toneClass}`}>
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-primary-light-300">{metric.label}</p>
        {metric.partial && (
          <span className="rounded-md border border-amber-300/25 px-1.5 py-0.5 text-[10px] font-medium text-amber-100">
            partial
          </span>
        )}
      </div>
      <p className="mt-2 text-2xl font-semibold text-primary-light-100">
        {formatMetricValue(metric)}
      </p>
      <p className="mt-1 text-xs leading-relaxed text-primary-light-500">{metric.description}</p>
    </article>
  );
};

const getBucketTone = (
  bucket: AdminRequestBucket
): 'default' | 'good' | 'warning' | 'danger' | 'neutral' => {
  if (bucket === 'terminal') return 'good';
  if (bucket === 'problematic') return 'danger';
  if (bucket === 'requiresClientAction' || bucket === 'requiresServiceAction') return 'warning';
  return 'default';
};

const getStatusTone = (
  request: AdminRequestDirectoryItem
): 'default' | 'good' | 'warning' | 'danger' | 'neutral' => {
  if (request.bucket === 'problematic') return 'danger';
  if (request.bucket === 'terminal') return 'good';
  if (request.bucket === 'requiresClientAction' || request.bucket === 'requiresServiceAction') {
    return 'warning';
  }
  return 'default';
};

const fieldValue = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return 'Brak';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Brak';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

const DetailField: React.FC<{ label: string; value: unknown; pre?: boolean }> = ({
  label,
  value,
  pre,
}) => (
  <div>
    <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-primary-light-500">
      {label}
    </p>
    {pre ? (
      <pre className="mt-1 max-h-56 overflow-auto whitespace-pre-wrap break-words rounded-md bg-primary-dark-100/70 p-2 text-xs leading-relaxed text-primary-light-300">
        {fieldValue(value)}
      </pre>
    ) : (
      <p className="mt-1 break-words text-sm text-primary-light-300">{fieldValue(value)}</p>
    )}
  </div>
);

const RequestDetail: React.FC<{
  request: AdminRequestDirectoryItem;
  busyKind?: 'archive' | 'restore';
  mutationError?: string;
  onArchive: () => void;
  onRestore: () => void;
}> = ({ request, busyKind, mutationError, onArchive, onRestore }) => {
  const raw = request.rawRequest;
  return (
    <div className="mt-3 rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-3">
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <DetailField label="Request id" value={request.id} />
        <DetailField label="clientId" value={raw.clientId} />
        <DetailField label="serviceProviderId" value={raw.serviceProviderId} />
        <DetailField label="Status" value={`${request.statusLabel} (${raw.status || 'brak'})`} />
        <DetailField label="workflowVersion" value={raw.workflowVersion || 'legacy/missing'} />
        <DetailField label="requestIndex" value={raw.requestIndex} />
        <DetailField label="serviceOperationalIndex" value={raw.serviceOperationalIndex} />
        <DetailField label="Fulfillment" value={raw.fulfillmentSnapshot} pre />
      </div>
      <div className="mt-3 grid grid-cols-1 gap-3 border-t border-primary-dark-300/55 pt-3 md:grid-cols-2 xl:grid-cols-4">
        <DetailField label="manualDeviceType" value={raw.manualDeviceType || raw.deviceType} />
        <DetailField label="brand/model" value={[raw.brand, raw.model].filter(Boolean).join(' ')} />
        <DetailField label="Opis" value={raw.description} />
        <DetailField label="Preliminary estimate" value={raw.preliminaryEstimate} pre />
        <DetailField label="Unread/activity" value={request.activitySummary} />
        <DetailField
          label="Timestamps"
          value={`createdAt: ${request.createdAtLabel}\nupdatedAt: ${request.updatedAtLabel}`}
          pre
        />
        <DetailField
          label="Legacy fields"
          value={{
            serviceId: raw.serviceId,
            deviceType: raw.deviceType,
            equipmentTypeNameSnapshot: raw.equipmentTypeNameSnapshot,
            subjectSnapshot: raw.subjectSnapshot,
            quotedPrice: raw.quotedPrice,
            quoteDescription: raw.quoteDescription,
          }}
          pre
        />
        <DetailField label="Ryzyka" value={request.riskLabels.join(', ')} />
        <DetailField
          label="Archiwum admina"
          value={
            request.isAdminArchived
              ? `Zarchiwizowane ${request.adminArchivedAtLabel}\nPowód: ${
                  request.adminArchiveReasonCode || 'brak'
                }`
              : 'Aktywne w monitorze'
          }
          pre
        />
      </div>
      <div className="mt-3 flex flex-col gap-2 border-t border-primary-dark-300/55 pt-3 sm:flex-row">
        {request.isAdminArchived ? (
          <button
            type="button"
            onClick={onRestore}
            disabled={Boolean(busyKind)}
            className="inline-flex min-h-[2.5rem] items-center justify-center rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100 disabled:opacity-50"
          >
            {busyKind === 'restore' ? 'Przywracanie...' : 'Przywróć do listy'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onArchive}
            disabled={Boolean(busyKind)}
            className="inline-flex min-h-[2.5rem] items-center justify-center rounded-lg border border-status-error-500/35 px-3 py-2 text-sm font-medium text-status-error-300 hover:bg-status-error-500/10 disabled:opacity-50"
          >
            {busyKind === 'archive' ? 'Archiwizowanie...' : 'Usuń z listy'}
          </button>
        )}
      </div>
      {mutationError && (
        <p className="mt-2 break-words text-sm text-status-error-300">{mutationError}</p>
      )}
    </div>
  );
};

const RequestRow: React.FC<{
  request: AdminRequestDirectoryItem;
  expanded: boolean;
  onToggle: () => void;
  busyKind?: 'archive' | 'restore';
  mutationError?: string;
  onArchive: () => void;
  onRestore: () => void;
  selectionMode: boolean;
  selected: boolean;
  onToggleSelected: () => void;
}> = ({
  request,
  expanded,
  onToggle,
  busyKind,
  mutationError,
  onArchive,
  onRestore,
  selectionMode,
  selected,
  onToggleSelected,
}) => (
  <article className="rounded-lg border border-primary-dark-300/18 bg-primary-dark-100/10 p-3.5">
    <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
      <div className="min-w-0 flex-1">
        {selectionMode && !request.isAdminArchived && (
          <label className="mb-3 inline-flex items-center gap-2 text-sm text-primary-light-300">
            <input
              type="checkbox"
              checked={selected}
              onChange={onToggleSelected}
              className="rounded border-primary-dark-300 bg-primary-dark-100 text-ratuj-300"
            />
            Zaznacz zgłoszenie
          </label>
        )}
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="max-w-full break-words text-base font-semibold text-primary-light-100">
            {request.displayTitle}
          </h2>
          <Badge tone={getStatusTone(request)}>{request.statusLabel}</Badge>
          <Badge tone={getBucketTone(request.bucket)}>{request.bucketLabel}</Badge>
          <Badge tone={request.workflowVersionLabel === 'V2' ? 'default' : 'neutral'}>
            {request.workflowVersionLabel}
          </Badge>
          {request.isAdminArchived && <Badge tone="neutral">Zarchiwizowane</Badge>}
        </div>
        <p className="mt-1 text-sm text-primary-light-400">
          {request.requestIndex || 'Brak requestIndex'}
          {request.serviceOperationalIndex ? ` · ${request.serviceOperationalIndex}` : ''}
        </p>
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs text-primary-light-500">
          <span>{request.serviceProviderName}</span>
          <span>{request.requesterLabel}</span>
          <span>Utworzono {request.createdAtLabel}</span>
          <span>Aktualizacja {request.updatedAtLabel}</span>
        </div>
        <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-primary-light-500">
          {request.fulfillmentSummary} · {request.activitySummary}
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {request.riskLabels.length ? (
            request.riskLabels.slice(0, 4).map((label) => (
              <Badge key={label} tone="warning">
                {label}
              </Badge>
            ))
          ) : (
            <Badge tone="good">Bez wykrytych ryzyk</Badge>
          )}
          {request.riskLabels.length > 4 && (
            <Badge tone="neutral">+{request.riskLabels.length - 4}</Badge>
          )}
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row xl:flex-shrink-0 xl:justify-end">
        <button
          type="button"
          onClick={onToggle}
          className="inline-flex min-h-[2.25rem] items-center justify-center gap-2 rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
          aria-expanded={expanded}
        >
          {expanded ? 'Zwiń' : 'Szczegóły'}
          {expanded ? (
            <ChevronUpIcon className="h-4 w-4" />
          ) : (
            <ChevronDownIcon className="h-4 w-4" />
          )}
        </button>
      </div>
    </div>
    {expanded && (
      <RequestDetail
        request={request}
        busyKind={busyKind}
        mutationError={mutationError}
        onArchive={onArchive}
        onRestore={onRestore}
      />
    )}
  </article>
);

const inCreatedRange = (request: AdminRequestDirectoryItem, range: CreatedRangeFilter): boolean => {
  if (range === 'all') return true;
  if (!request.createdAt) return false;
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const cutoff = Date.now() - days * 24 * 60 * 60 * 1000;
  return request.createdAt.getTime() >= cutoff;
};

const sortRequests = (
  requests: AdminRequestDirectoryItem[],
  sort: SortOption
): AdminRequestDirectoryItem[] => {
  const dateMs = (date: Date | null) => date?.getTime() || 0;
  return [...requests].sort((a, b) => {
    if (sort === 'created_desc') return dateMs(b.createdAt) - dateMs(a.createdAt);
    if (sort === 'created_asc') return dateMs(a.createdAt) - dateMs(b.createdAt);
    if (sort === 'status_asc') {
      return (statusOrder[String(a.status)] ?? 99) - (statusOrder[String(b.status)] ?? 99);
    }
    if (sort === 'index_asc') {
      const left = a.serviceOperationalIndex || a.requestIndex || a.id;
      const right = b.serviceOperationalIndex || b.requestIndex || b.id;
      return left.localeCompare(right, 'pl');
    }
    return dateMs(b.updatedAt) - dateMs(a.updatedAt);
  });
};

const AdminRequestsPage: React.FC = () => {
  const {
    data,
    requests,
    loading,
    loadingMore,
    error,
    refresh,
    loadMore,
    archiveRequest,
    archiveRequestsBatch,
    restoreRequest,
    mutationByRequestId,
    mutationErrorByRequestId,
  } = useAdminRequests();
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [archiveView, setArchiveView] = useState<AdminRequestArchiveView>('active');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [bucketFilter, setBucketFilter] = useState<BucketFilter>('all');
  const [fulfillmentFilter, setFulfillmentFilter] = useState<FulfillmentFilter>('all');
  const [createdRange, setCreatedRange] = useState<CreatedRangeFilter>('30d');
  const [sort, setSort] = useState<SortOption>('updated_desc');
  const [modalState, setModalState] = useState<ArchiveModalState>(null);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRequestIds, setSelectedRequestIds] = useState<string[]>([]);
  const [archiveReasonCode, setArchiveReasonCode] =
    useState<AdminRequestArchiveReasonCode>('test_cleanup');
  const [modalNote, setModalNote] = useState('');

  const filteredRequests = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = filterRequestsByArchiveView(requests, archiveView).filter((request) => {
      if (q && !request.searchText.includes(q)) return false;
      if (statusFilter !== 'all' && request.status !== statusFilter) return false;
      if (bucketFilter !== 'all' && request.bucket !== bucketFilter) return false;
      if (fulfillmentFilter === 'missing' && request.fulfillmentMode) return false;
      if (
        fulfillmentFilter !== 'all' &&
        fulfillmentFilter !== 'missing' &&
        request.fulfillmentMode !== fulfillmentFilter
      ) {
        return false;
      }
      if (!inCreatedRange(request, createdRange)) return false;
      return true;
    });
    return sortRequests(filtered, sort);
  }, [
    archiveView,
    bucketFilter,
    createdRange,
    fulfillmentFilter,
    requests,
    search,
    sort,
    statusFilter,
  ]);

  const openArchiveModal = (request: AdminRequestDirectoryItem) => {
    setArchiveReasonCode('test_cleanup');
    setModalNote('');
    setModalState({ kind: 'archive', request });
  };

  const openRestoreModal = (request: AdminRequestDirectoryItem) => {
    setModalNote('');
    setModalState({ kind: 'restore', request });
  };

  const selectableRequests = filteredRequests.filter((request) => !request.isAdminArchived);
  const selectedRequests = selectableRequests.filter((request) =>
    selectedRequestIds.includes(request.id)
  );

  const toggleSelectedRequest = (requestId: string) => {
    setSelectedRequestIds((current) =>
      current.includes(requestId)
        ? current.filter((currentId) => currentId !== requestId)
        : [...current, requestId]
    );
  };

  const toggleSelectAllVisible = () => {
    const visibleIds = selectableRequests.map((request) => request.id);
    const allVisibleSelected =
      visibleIds.length > 0 &&
      visibleIds.every((requestId) => selectedRequestIds.includes(requestId));
    setSelectedRequestIds((current) =>
      allVisibleSelected
        ? current.filter((requestId) => !visibleIds.includes(requestId))
        : Array.from(new Set([...current, ...visibleIds]))
    );
  };

  const openBatchArchiveModal = () => {
    setArchiveReasonCode('test_cleanup');
    setModalNote('');
    setModalState({ kind: 'archiveBatch', requests: selectedRequests });
  };

  const closeModal = () => setModalState(null);

  const confirmModal = async () => {
    if (!modalState) return;
    let success = false;
    if (modalState.kind === 'archive') {
      success = await archiveRequest(
        modalState.request.id,
        archiveReasonCode,
        modalNote.trim() || undefined
      );
    } else if (modalState.kind === 'archiveBatch') {
      success = await archiveRequestsBatch(
        modalState.requests.map((request) => request.id),
        archiveReasonCode,
        modalNote.trim() || undefined
      );
    } else {
      success = await restoreRequest(modalState.request.id, modalNote.trim() || undefined);
    }
    if (success) {
      if (modalState.kind === 'archiveBatch') {
        setSelectedRequestIds([]);
        setSelectionMode(false);
      }
      closeModal();
    }
  };

  return (
    <div className="space-y-5 pb-24">
      <section className="border-b border-primary-dark-300/18 px-1 pb-4 sm:px-0 sm:pb-5">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-ratuj-200">
              Admin Operations
            </p>
            <h1 className="mt-1 text-2xl font-semibold text-primary-light-100">Zgłoszenia</h1>
            <p className="mt-2 max-w-2xl text-sm leading-relaxed text-primary-light-400">
              Monitor procesu napraw i statusów zgłoszeń
            </p>
          </div>
          <button
            type="button"
            onClick={() => void refresh()}
            className="inline-flex min-h-[2.5rem] items-center justify-center gap-2 rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
          >
            <ArrowPathIcon className="h-4 w-4" />
            Odśwież
          </button>
        </div>
      </section>

      {error && (
        <div className="rounded-lg border border-status-error-500/35 bg-status-error-500/10 p-3 text-sm text-status-error-300">
          <div className="flex items-start gap-2">
            <ExclamationTriangleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {data?.partialError && (
        <div className="rounded-lg border border-amber-300/30 bg-amber-300/10 p-3 text-sm text-amber-100">
          {data.partialError}
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="h-28 animate-pulse rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/70"
            />
          ))}
        </div>
      ) : data ? (
        <>
          <section className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
            <MetricTile metric={data.metrics.total} />
            <MetricTile metric={data.metrics.active} />
            <MetricTile metric={data.metrics.new} />
            <MetricTile metric={data.metrics.requiresClientAction} tone="warning" />
            <MetricTile metric={data.metrics.requiresServiceAction} tone="warning" />
            <MetricTile metric={data.metrics.inProgress} />
            <MetricTile metric={data.metrics.terminal} tone="good" />
            <MetricTile metric={data.metrics.problematic} tone="warning" />
          </section>

          <section className="border-t border-primary-dark-300/16 px-1 py-4 sm:px-0 sm:py-5">
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-primary-light-100">
              <FunnelIcon className="h-4 w-4 text-ratuj-200" />
              Filtry
            </div>
            <div className="grid grid-cols-1 gap-3 lg:grid-cols-7">
              <label className="lg:col-span-2">
                <span className="text-xs font-medium text-primary-light-400">Szukaj</span>
                <div className="mt-1 flex min-h-[2.5rem] items-center gap-2 rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3">
                  <MagnifyingGlassIcon className="h-4 w-4 text-primary-light-500" />
                  <input
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    placeholder="Index, sprzęt, serwis, klient"
                    className="min-w-0 flex-1 bg-transparent text-sm text-primary-light-100 outline-none placeholder:text-primary-light-500"
                  />
                </div>
              </label>
              <label>
                <span className="text-xs font-medium text-primary-light-400">Widok</span>
                <select
                  aria-label="Widok archiwum"
                  value={archiveView}
                  onChange={(event) =>
                    setArchiveView(event.target.value as AdminRequestArchiveView)
                  }
                  className="mt-1 min-h-[2.5rem] w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
                >
                  <option value="active">Aktywne</option>
                  <option value="archived">Zarchiwizowane</option>
                  <option value="all">Wszystkie</option>
                </select>
              </label>
              <label>
                <span className="text-xs font-medium text-primary-light-400">Status</span>
                <select
                  aria-label="Status"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
                  className="mt-1 min-h-[2.5rem] w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
                >
                  <option value="all">Wszystkie</option>
                  {REPAIR_STATUS_ORDER.map((status) => (
                    <option key={status} value={status}>
                      {getRepairRequestStatusLabel(status)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span className="text-xs font-medium text-primary-light-400">Bucket</span>
                <select
                  aria-label="Bucket"
                  value={bucketFilter}
                  onChange={(event) => setBucketFilter(event.target.value as BucketFilter)}
                  className="mt-1 min-h-[2.5rem] w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
                >
                  <option value="all">Wszystkie</option>
                  <option value="active">Aktywne</option>
                  <option value="requiresClientAction">Reakcja klienta</option>
                  <option value="requiresServiceAction">Reakcja serwisu</option>
                  <option value="terminal">Zakończone</option>
                  <option value="problematic">Legacy/problemy</option>
                </select>
              </label>
              <label>
                <span className="text-xs font-medium text-primary-light-400">Realizacja</span>
                <select
                  aria-label="Realizacja"
                  value={fulfillmentFilter}
                  onChange={(event) =>
                    setFulfillmentFilter(event.target.value as FulfillmentFilter)
                  }
                  className="mt-1 min-h-[2.5rem] w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
                >
                  <option value="all">Wszystkie</option>
                  <option value="customerDropoff">Dostarczenie</option>
                  <option value="servicePickup">Odbiór</option>
                  <option value="onsiteService">U klienta</option>
                  <option value="missing">Brak snapshotu</option>
                </select>
              </label>
              <label>
                <span className="text-xs font-medium text-primary-light-400">Utworzono</span>
                <select
                  aria-label="Utworzono"
                  value={createdRange}
                  onChange={(event) => setCreatedRange(event.target.value as CreatedRangeFilter)}
                  className="mt-1 min-h-[2.5rem] w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
                >
                  <option value="7d">Ostatnie 7 dni</option>
                  <option value="30d">Ostatnie 30 dni</option>
                  <option value="90d">Ostatnie 90 dni</option>
                  <option value="all">Wszystkie</option>
                </select>
              </label>
              <label>
                <span className="text-xs font-medium text-primary-light-400">Sortowanie</span>
                <select
                  aria-label="Sortowanie"
                  value={sort}
                  onChange={(event) => setSort(event.target.value as SortOption)}
                  className="mt-1 min-h-[2.5rem] w-full rounded-lg border border-primary-dark-300 bg-primary-dark-100 px-3 text-sm text-primary-light-100"
                >
                  <option value="updated_desc">Ostatnio aktualizowane</option>
                  <option value="created_desc">Najnowsze</option>
                  <option value="created_asc">Najstarsze</option>
                  <option value="status_asc">Status</option>
                  <option value="index_asc">Index</option>
                </select>
              </label>
            </div>
          </section>

          <section className="border-t border-primary-dark-300/16 px-1 py-4 sm:px-0 sm:py-5">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-base font-semibold text-primary-light-100">
                  Wyniki ({filteredRequests.length})
                </h2>
                <p className="mt-1 text-xs text-primary-light-500">
                  Załadowano {requests.length} rekordów. Query bazowe: updatedAt desc, limit{' '}
                  {data.readLimits.pageSize}. Legacy bez pól archiwizacji traktowane są jako
                  aktywne.
                </p>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row">
                {archiveView === 'active' && (
                  <button
                    type="button"
                    onClick={() => {
                      setSelectionMode((current) => !current);
                      setSelectedRequestIds([]);
                    }}
                    className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100"
                  >
                    {selectionMode ? 'Zakończ zaznaczanie' : 'Zaznacz wiele'}
                  </button>
                )}
                {data.hasMore && (
                  <button
                    type="button"
                    onClick={() => void loadMore()}
                    disabled={loadingMore}
                    className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200 hover:border-ratuj-300/50 hover:text-ratuj-100 disabled:opacity-50"
                  >
                    {loadingMore ? 'Ładowanie...' : 'Załaduj więcej'}
                  </button>
                )}
              </div>
            </div>

            {selectionMode && archiveView === 'active' && (
              <div className="mt-4 flex flex-col gap-3 rounded-lg border border-primary-dark-300/55 bg-primary-dark-300/20 p-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                  <button
                    type="button"
                    onClick={toggleSelectAllVisible}
                    className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200"
                  >
                    Zaznacz widoczne
                  </button>
                  <p className="text-sm text-primary-light-400">
                    Zaznaczono {selectedRequests.length}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={openBatchArchiveModal}
                  disabled={selectedRequests.length === 0}
                  className="inline-flex min-h-[2.25rem] items-center justify-center rounded-lg border border-status-error-500/35 px-3 py-2 text-sm font-medium text-status-error-300 hover:bg-status-error-500/10 disabled:opacity-50"
                >
                  Usuń zaznaczone z listy
                </button>
              </div>
            )}

            {requests.length === 0 ? (
              <div className="mt-4 rounded-lg border border-primary-dark-300/55 bg-primary-dark-300/20 p-5 text-sm text-primary-light-400">
                Brak dokumentów repairRequests do wyświetlenia.
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="mt-4 rounded-lg border border-primary-dark-300/55 bg-primary-dark-300/20 p-5 text-sm text-primary-light-400">
                Brak wyników dla aktualnych filtrów.
              </div>
            ) : (
              <div className="mt-4 space-y-3">
                {filteredRequests.map((request) => (
                  <RequestRow
                    key={request.id}
                    request={request}
                    expanded={expandedId === request.id}
                    onToggle={() =>
                      setExpandedId((current) => (current === request.id ? null : request.id))
                    }
                    busyKind={mutationByRequestId[request.id]}
                    mutationError={mutationErrorByRequestId[request.id]}
                    onArchive={() => openArchiveModal(request)}
                    onRestore={() => openRestoreModal(request)}
                    selectionMode={selectionMode && archiveView === 'active'}
                    selected={selectedRequestIds.includes(request.id)}
                    onToggleSelected={() => toggleSelectedRequest(request.id)}
                  />
                ))}
              </div>
            )}
          </section>
        </>
      ) : (
        <div className="rounded-lg border border-primary-dark-300/60 bg-primary-dark-200/70 p-5 text-sm text-primary-light-400">
          Nie udało się przygotować read modelu zgłoszeń.
        </div>
      )}
      {modalState && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-lg border border-primary-dark-300 bg-primary-dark-100 p-4 shadow-xl">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 className="break-words text-lg font-semibold text-primary-light-100">
                  {modalState.kind === 'archive' || modalState.kind === 'archiveBatch'
                    ? 'Archiwizować zgłoszenie?'
                    : 'Przywrócić zgłoszenie do listy?'}
                </h2>
                <p className="mt-2 break-words text-sm leading-relaxed text-primary-light-400">
                  {modalState.kind === 'archive' || modalState.kind === 'archiveBatch'
                    ? `${modalState.kind === 'archiveBatch' ? `Wybrane zgłoszenia (${modalState.requests.length}) znikną` : 'Zgłoszenie zniknie'} z domyślnej listy admina. Dane, czat i załączniki pozostają w systemie do audytu.`
                    : 'Zgłoszenie wróci do domyślnej listy admina. Workflow i dane zgłoszenia nie zostaną zmienione.'}
                </p>
              </div>
              <button
                type="button"
                onClick={closeModal}
                className="rounded-md p-1 text-primary-light-400 hover:text-primary-light-100"
                aria-label="Zamknij"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {(modalState.kind === 'archive' || modalState.kind === 'archiveBatch') && (
              <label className="mt-4 block">
                <span className="text-xs font-medium text-primary-light-400">Powód</span>
                <select
                  aria-label="Powód archiwizacji"
                  value={archiveReasonCode}
                  onChange={(event) =>
                    setArchiveReasonCode(event.target.value as AdminRequestArchiveReasonCode)
                  }
                  className="mt-1 min-h-[2.5rem] w-full rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 text-sm text-primary-light-100"
                >
                  {ARCHIVE_REASON_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            )}

            <label className="mt-4 block">
              <span className="text-xs font-medium text-primary-light-400">
                {modalState.kind === 'archive' || modalState.kind === 'archiveBatch'
                  ? 'Notatka'
                  : 'Notatka przywrócenia'}
              </span>
              <textarea
                value={modalNote}
                onChange={(event) => setModalNote(event.target.value)}
                maxLength={1000}
                rows={3}
                className="mt-1 w-full resize-y rounded-lg border border-primary-dark-300 bg-primary-dark-200 px-3 py-2 text-sm text-primary-light-100"
              />
            </label>

            <div className="mt-4 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={closeModal}
                className="inline-flex min-h-[2.5rem] items-center justify-center rounded-lg border border-primary-dark-300 px-3 py-2 text-sm font-medium text-primary-light-200"
              >
                Anuluj
              </button>
              <button
                type="button"
                onClick={() => void confirmModal()}
                disabled={
                  modalState.kind === 'archiveBatch'
                    ? Boolean(
                        mutationByRequestId[
                          modalState.requests
                            .map((request) => request.id)
                            .sort()
                            .join('|')
                        ]
                      )
                    : Boolean(mutationByRequestId[modalState.request.id])
                }
                className={`inline-flex min-h-[2.5rem] items-center justify-center rounded-lg border px-3 py-2 text-sm font-medium disabled:opacity-50 ${
                  modalState.kind === 'archive' || modalState.kind === 'archiveBatch'
                    ? 'border-status-error-500/35 text-status-error-300 hover:bg-status-error-500/10'
                    : 'border-ratuj-300/40 text-ratuj-100 hover:bg-ratuj-300/10'
                }`}
              >
                {modalState.kind === 'archive' || modalState.kind === 'archiveBatch'
                  ? 'Archiwizuj'
                  : 'Przywróć'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminRequestsPage;
